import { useEffect, useMemo, useRef, useState } from "react";
import {
  Loader2, Plus, Upload, Trash2, Pencil, X, Save, Users, Download, Settings2,
  Tag, CalendarClock, CheckSquare, Square, Phone, Mail, StickyNote, CalendarDays, GripVertical,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

const money = (n) => `$${Math.round(n || 0).toLocaleString()}`;
const STAGE_COLOR = {
  New: "bg-[#1e3d3a]/10 text-[#1e3d3a]", Contacted: "bg-[#c9a961]/20 text-[#8a6d2f]",
  Proposal: "bg-[#4d5a24]/15 text-[#4d5a24]", Won: "bg-[#2f7d5b]/15 text-[#2f7d5b]",
  Lost: "bg-[#b23a48]/12 text-[#b23a48]",
};
const stageColor = (s) => STAGE_COLOR[s] || "bg-[#c9a961]/15 text-[#8a6d2f]";
const ACT_ICON = { note: StickyNote, call: Phone, email: Mail, meeting: CalendarDays };
const BLANK = { name: "", company: "", email: "", phone: "", stage: "New", value: "", notes: "", tags: "" };
const inputCls = "w-full rounded-lg border border-[#1e3d3a]/15 bg-white px-3 py-2 text-sm text-[#1e3d3a] focus:border-[#c9a961] focus:outline-none";
const today = () => new Date().toISOString().slice(0, 10);

export default function PortalCRM() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(null);
  const [detail, setDetail] = useState(null);
  const [stageEdit, setStageEdit] = useState(null);
  const [filter, setFilter] = useState("all");
  const [tagFilter, setTagFilter] = useState("all");
  const fileRef = useRef(null);

  const load = async (keepId) => {
    try {
      const { data } = await api.get("/portal/crm");
      setData(data);
      if (keepId) setDetail(data.contacts.find((c) => c.id === keepId) || null);
    } catch { toast.error("Could not load your CRM."); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    if (!form.name?.trim() && !form.company?.trim()) return toast.error("Add a name or company.");
    setBusy(true);
    try {
      const body = {
        ...form, value: Number(form.value) || 0,
        tags: (form.tags || "").split(",").map((t) => t.trim()).filter(Boolean),
      };
      if (form.id) await api.put(`/portal/crm/contacts/${form.id}`, body);
      else await api.post("/portal/crm/contacts", body);
      setForm(null); await load(); toast.success(form.id ? "Contact updated." : "Contact added.");
    } catch { toast.error("Could not save."); }
    finally { setBusy(false); }
  };

  const del = async (id) => {
    try { await api.delete(`/portal/crm/contacts/${id}`); setDetail(null); await load(); toast.success("Removed."); }
    catch { toast.error("Could not remove."); }
  };

  const moveStage = async (c, stage) => {
    if (!stage || stage === c.stage) return;
    setData((d) => d ? { ...d, contacts: d.contacts.map((x) => x.id === c.id ? { ...x, stage } : x) } : d);
    try {
      await api.put(`/portal/crm/contacts/${c.id}`, { ...c, value: Number(c.value) || 0, stage });
      await load(); toast.success(`Moved to ${stage}.`);
    } catch { toast.error("Could not move."); await load(); }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0]; if (file) e.target.value = "";
    if (!file) return;
    setBusy(true); toast.message("Reading your file with AI…");
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/portal/crm/import", fd);
      await load(); toast.success(`Added ${data.added} contact${data.added === 1 ? "" : "s"}.`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not read that file."); }
    finally { setBusy(false); }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/portal/crm/export", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = "my-crm-contacts.csv"; document.body.appendChild(a); a.click();
      a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error("Could not export."); }
  };

  // ---- upcoming tasks across all contacts ----
  const upcoming = useMemo(() => {
    if (!data) return [];
    const rows = [];
    for (const c of data.contacts) for (const t of (c.tasks || [])) {
      if (!t.done) rows.push({ ...t, contact: c });
    }
    rows.sort((a, b) => (a.due || "9999").localeCompare(b.due || "9999"));
    return rows.slice(0, 5);
  }, [data]);

  if (loading) return <div className="flex items-center gap-2 text-[#1e3d3a]/60 py-10"><Loader2 className="h-4 w-4 animate-spin" /> Loading your CRM…</div>;
  if (!data) return null;
  const s = data.summary;
  const stages = data.stages || [];
  let contacts = filter === "all" ? data.contacts : data.contacts.filter((c) => c.stage === filter);
  if (tagFilter !== "all") contacts = contacts.filter((c) => (c.tags || []).includes(tagFilter));

  return (
    <div data-testid="portal-crm" className="space-y-6">
      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="crm-summary">
        {[["Contacts", s.total, ""], ["Open pipeline", s.open_value, "$"], ["Won", s.won_value, "$"], ["Win rate", s.win_rate, "%"]].map(([lbl, val, u]) => (
          <div key={lbl} className="rounded-xl border border-[#1e3d3a]/12 bg-white px-4 py-3">
            <div className="text-[9px] uppercase tracking-wide text-[#1e3d3a]/45">{lbl}</div>
            <div className="mt-1 font-head text-xl text-[#1e3d3a] tabular-nums">{u === "$" ? money(val) : u === "%" ? `${val}%` : val}</div>
          </div>
        ))}
      </div>

      {/* Upcoming follow-ups */}
      {upcoming.length > 0 && (
        <div className="rounded-2xl border border-[#c9a961]/30 bg-[#c9a961]/8 p-4" data-testid="crm-reminders">
          <div className="flex items-center gap-2 text-[#8a6d2f] mb-2"><CalendarClock className="h-4 w-4" /><h3 className="font-head text-base text-[#1e3d3a]">Upcoming follow-ups</h3></div>
          <div className="space-y-1.5">
            {upcoming.map((t) => {
              const overdue = t.due && t.due < today();
              return (
                <button key={t.id} data-testid={`crm-reminder-${t.id}`} onClick={() => setDetail(t.contact)}
                  className="w-full flex items-center justify-between gap-3 rounded-lg bg-white/70 px-3 py-1.5 text-left hover:bg-white transition-colors">
                  <span className="text-sm text-[#1e3d3a] truncate">{t.title} <span className="text-[#1e3d3a]/45">· {t.contact.name}</span></span>
                  <span className={`text-xs whitespace-nowrap ${overdue ? "text-[#b23a48] font-semibold" : "text-[#1e3d3a]/55"}`}>{t.due ? (overdue ? "Overdue · " : "") + t.due : "No date"}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Pipeline chips */}
      <div className="rounded-2xl border border-[#1e3d3a]/12 bg-white p-4" data-testid="crm-pipeline">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[10px] uppercase tracking-wide text-[#1e3d3a]/45">Pipeline</span>
          <button data-testid="crm-edit-stages" onClick={() => setStageEdit([...stages])} className="inline-flex items-center gap-1 text-[11px] text-[#1e3d3a]/50 hover:text-[#c9a961] transition-colors"><Settings2 className="h-3 w-3" /> Edit stages</button>
        </div>
        <div className="flex flex-wrap gap-2">
          {s.by_stage.map((st) => (
            <div key={st.stage} className={`rounded-xl px-3 py-2 ${stageColor(st.stage)}`}>
              <div className="text-[10px] uppercase tracking-wide opacity-80">{st.stage}</div>
              <div className="text-sm font-semibold tabular-nums">{st.count} · {money(st.value)}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Contacts */}
      <div className="rounded-2xl border border-[#1e3d3a]/12 bg-white p-5 lg:p-6" data-testid="crm-contacts">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div className="flex items-center gap-2 text-[#1e3d3a]"><Users className="h-4 w-4 text-[#c9a961]" /><h3 className="font-head text-lg">Contacts & leads</h3></div>
          <div className="flex items-center gap-2">
            <input ref={fileRef} type="file" className="hidden" data-testid="crm-upload-input" onChange={onUpload} accept=".csv,.txt,.pdf,.png,.jpg,.jpeg,.webp,.xlsx,.xls" />
            <button data-testid="crm-export-btn" onClick={exportCsv} className="inline-flex items-center gap-1.5 rounded-full border border-[#1e3d3a]/20 px-3 py-1.5 text-xs text-[#1e3d3a]/75 hover:border-[#c9a961] transition-colors"><Download className="h-3.5 w-3.5" /> Export</button>
            <button data-testid="crm-upload-btn" disabled={busy} onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-1.5 rounded-full border border-[#1e3d3a]/20 px-3 py-1.5 text-xs text-[#1e3d3a]/75 hover:border-[#c9a961] disabled:opacity-50 transition-colors">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Import
            </button>
            <button data-testid="crm-add-btn" onClick={() => setForm({ ...BLANK, stage: stages[0] || "New" })} className="inline-flex items-center gap-1.5 rounded-full bg-[#1e3d3a] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#2a534d] transition-colors"><Plus className="h-3.5 w-3.5" /> Add</button>
          </div>
        </div>

        <div className="mb-2 flex flex-wrap gap-1.5">
          {["all", ...stages].map((c) => (
            <button key={c} data-testid={`crm-filter-${c}`} onClick={() => setFilter(c)} className={`rounded-full px-2.5 py-1 text-[10px] uppercase tracking-wide border transition-colors ${filter === c ? "border-[#c9a961] bg-[#c9a961]/15 text-[#1e3d3a]" : "border-[#1e3d3a]/12 text-[#1e3d3a]/50"}`}>{c}</button>
          ))}
        </div>
        {data.tags?.length > 0 && (
          <div className="mb-3 flex flex-wrap items-center gap-1.5">
            <Tag className="h-3 w-3 text-[#1e3d3a]/35" />
            {["all", ...data.tags].map((t) => (
              <button key={t} data-testid={`crm-tagfilter-${t}`} onClick={() => setTagFilter(t)} className={`rounded-full px-2.5 py-0.5 text-[10px] border transition-colors ${tagFilter === t ? "border-[#c9a961] bg-[#c9a961]/15 text-[#1e3d3a]" : "border-[#1e3d3a]/12 text-[#1e3d3a]/45"}`}>{t === "all" ? "All tags" : t}</button>
            ))}
          </div>
        )}

        {contacts.length ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[640px]">
              <thead><tr className="text-[#1e3d3a]/45 border-b border-[#1e3d3a]/10 text-xs">
                <th className="py-2 text-left font-medium">Name</th><th className="py-2 text-left font-medium">Company</th>
                <th className="py-2 text-left font-medium">Contact</th><th className="py-2 text-left font-medium">Stage</th>
                <th className="py-2 text-right font-medium">Value</th><th className="py-2"></th>
              </tr></thead>
              <tbody>
                {contacts.map((c) => {
                  const openTasks = (c.tasks || []).filter((t) => !t.done).length;
                  return (
                    <tr key={c.id} data-testid={`crm-row-${c.id}`} className="border-b border-[#1e3d3a]/6">
                      <td className="py-1.5 text-[#1e3d3a]/85">
                        <button data-testid={`crm-open-${c.id}`} onClick={() => setDetail(c)} className="hover:text-[#c9a961] transition-colors text-left font-medium">{c.name}</button>
                        {c.added_by === "elevare" && <span className="ml-1.5 text-[9px] uppercase text-[#c9a961]">Elevare</span>}
                        <div className="flex flex-wrap gap-1 mt-0.5">
                          {(c.tags || []).map((t) => <span key={t} className="rounded-full bg-[#c9a961]/15 px-1.5 py-0.5 text-[9px] text-[#8a6d2f]">{t}</span>)}
                          {openTasks > 0 && <span className="inline-flex items-center gap-0.5 rounded-full bg-[#1e3d3a]/8 px-1.5 py-0.5 text-[9px] text-[#1e3d3a]/60"><CalendarClock className="h-2.5 w-2.5" />{openTasks}</span>}
                        </div>
                      </td>
                      <td className="py-1.5 text-[#1e3d3a]/60">{c.company || "—"}</td>
                      <td className="py-1.5 text-[#1e3d3a]/55 text-xs">{c.email || c.phone || "—"}</td>
                      <td className="py-1.5">
                        <select data-testid={`crm-move-${c.id}`} value={c.stage} onChange={(e) => moveStage(c, e.target.value)}
                          className={`rounded-full px-2 py-0.5 text-[10px] uppercase border-0 cursor-pointer focus:outline-none focus:ring-1 focus:ring-[#c9a961] ${stageColor(c.stage)}`}
                          title="Move to another stage">
                          {stages.map((st) => <option key={st} value={st}>{st}</option>)}
                          {!stages.includes(c.stage) && <option value={c.stage}>{c.stage}</option>}
                        </select>
                      </td>
                      <td className="py-1.5 text-right tabular-nums">{money(c.value)}</td>
                      <td className="py-1.5 text-right whitespace-nowrap">
                        <button data-testid={`crm-edit-${c.id}`} onClick={() => setForm({ ...c, value: String(c.value), tags: (c.tags || []).join(", ") })} className="p-1 text-[#1e3d3a]/40 hover:text-[#c9a961]"><Pencil className="h-3.5 w-3.5" /></button>
                        <button data-testid={`crm-del-${c.id}`} onClick={() => del(c.id)} className="p-1 text-[#1e3d3a]/40 hover:text-[#b23a48]"><Trash2 className="h-3.5 w-3.5" /></button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : <p className="text-sm text-[#1e3d3a]/50 py-8 text-center">No contacts yet. Add one, or import a CSV/list to auto-fill your pipeline.</p>}
      </div>

      {/* Add / edit modal */}
      {form && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="crm-modal" onClick={() => setForm(null)}>
          <div className="w-full max-w-md rounded-2xl bg-white p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4"><h3 className="font-head text-xl text-[#1e3d3a]">{form.id ? "Edit contact" : "Add contact"}</h3><button data-testid="crm-modal-close" onClick={() => setForm(null)} className="text-[#1e3d3a]/40 hover:text-[#1e3d3a]"><X className="h-5 w-5" /></button></div>
            <div className="grid grid-cols-2 gap-3">
              {[["name", "Name", "col-span-2"], ["company", "Company", "col-span-2"], ["email", "Email", ""], ["phone", "Phone", ""], ["value", "Deal value ($)", ""]].map(([k, lbl, cls]) => (
                <div key={k} className={cls}>
                  <label className="block text-[10px] uppercase tracking-wide text-[#1e3d3a]/50 mb-1">{lbl}</label>
                  <input data-testid={`crm-field-${k}`} type={k === "value" ? "number" : "text"} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className={inputCls} />
                </div>
              ))}
              <div>
                <label className="block text-[10px] uppercase tracking-wide text-[#1e3d3a]/50 mb-1">Stage</label>
                <select data-testid="crm-field-stage" value={form.stage} onChange={(e) => setForm({ ...form, stage: e.target.value })} className={inputCls}>{stages.map((st) => <option key={st} value={st}>{st}</option>)}</select>
              </div>
              <div className="col-span-2">
                <label className="block text-[10px] uppercase tracking-wide text-[#1e3d3a]/50 mb-1">Tags (comma-separated)</label>
                <input data-testid="crm-field-tags" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} className={inputCls} placeholder="VIP, Vendor, Referral" />
              </div>
              <div className="col-span-2">
                <label className="block text-[10px] uppercase tracking-wide text-[#1e3d3a]/50 mb-1">Notes</label>
                <textarea data-testid="crm-field-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={inputCls} rows={2} />
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setForm(null)} className="rounded-full border border-[#1e3d3a]/20 px-4 py-2 text-sm text-[#1e3d3a]/70">Cancel</button>
              <button data-testid="crm-modal-save" disabled={busy} onClick={save} className="inline-flex items-center gap-1.5 rounded-full bg-[#c9a961] px-4 py-2 text-sm font-semibold text-[#1a2e2b] hover:bg-[#d8b972] disabled:opacity-50">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save</button>
            </div>
          </div>
        </div>
      )}

      {detail && <DetailModal contact={detail} onClose={() => setDetail(null)} reload={() => load(detail.id)} onEdit={() => { setForm({ ...detail, value: String(detail.value), tags: (detail.tags || []).join(", ") }); setDetail(null); }} />}
      {stageEdit && <StagesModal initial={stageEdit} onClose={() => setStageEdit(null)} onSaved={async () => { setStageEdit(null); await load(); }} />}
    </div>
  );
}

// ---------------- Contact detail: activity timeline + tasks ----------------
function DetailModal({ contact, onClose, reload, onEdit }) {
  const c = contact;
  const [act, setAct] = useState({ type: "note", note: "", date: today() });
  const [taskTitle, setTaskTitle] = useState("");
  const [taskDue, setTaskDue] = useState("");
  const [busy, setBusy] = useState(false);

  const addActivity = async () => {
    if (!act.note.trim()) return toast.error("Add a note.");
    setBusy(true);
    try { await api.post(`/portal/crm/contacts/${c.id}/activities`, act); setAct({ type: "note", note: "", date: today() }); await reload(); }
    catch { toast.error("Could not add."); } finally { setBusy(false); }
  };
  const delActivity = async (aid) => { try { await api.delete(`/portal/crm/contacts/${c.id}/activities/${aid}`); await reload(); } catch { toast.error("Could not remove."); } };
  const addTask = async () => {
    if (!taskTitle.trim()) return toast.error("Add a task.");
    setBusy(true);
    try { await api.post(`/portal/crm/contacts/${c.id}/tasks`, { title: taskTitle, due: taskDue || null }); setTaskTitle(""); setTaskDue(""); await reload(); }
    catch { toast.error("Could not add."); } finally { setBusy(false); }
  };
  const toggleTask = async (t) => { try { await api.put(`/portal/crm/contacts/${c.id}/tasks/${t.id}`, { done: !t.done }); await reload(); } catch { toast.error("Could not update."); } };
  const delTask = async (tid) => { try { await api.delete(`/portal/crm/contacts/${c.id}/tasks/${tid}`); await reload(); } catch { toast.error("Could not remove."); } };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="crm-detail" onClick={onClose}>
      <div className="w-full max-w-2xl max-h-[88vh] overflow-y-auto rounded-2xl bg-white p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-1">
          <div>
            <h3 className="font-head text-2xl text-[#1e3d3a]">{c.name}</h3>
            <p className="text-sm text-[#1e3d3a]/55">{[c.company, c.email, c.phone].filter(Boolean).join(" · ") || "No contact details"}</p>
          </div>
          <div className="flex items-center gap-1">
            <button data-testid="crm-detail-edit" onClick={onEdit} className="p-1.5 text-[#1e3d3a]/45 hover:text-[#c9a961]"><Pencil className="h-4 w-4" /></button>
            <button data-testid="crm-detail-close" onClick={onClose} className="p-1.5 text-[#1e3d3a]/45 hover:text-[#1e3d3a]"><X className="h-5 w-5" /></button>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-1.5 mb-5">
          <span className={`rounded-full px-2 py-0.5 text-[10px] uppercase ${stageColor(c.stage)}`}>{c.stage}</span>
          <span className="rounded-full bg-[#1e3d3a]/8 px-2 py-0.5 text-[10px] text-[#1e3d3a]/70">{money(c.value)}</span>
          {(c.tags || []).map((t) => <span key={t} className="rounded-full bg-[#c9a961]/15 px-2 py-0.5 text-[10px] text-[#8a6d2f]">{t}</span>)}
        </div>
        {c.notes && <p className="mb-5 rounded-lg bg-[#f4f1e9] px-3 py-2 text-sm text-[#1e3d3a]/75 whitespace-pre-wrap">{c.notes}</p>}

        <div className="grid md:grid-cols-2 gap-6">
          {/* Tasks */}
          <div data-testid="crm-tasks">
            <h4 className="font-head text-base text-[#1e3d3a] mb-2 flex items-center gap-1.5"><CalendarClock className="h-4 w-4 text-[#c9a961]" /> Tasks & follow-ups</h4>
            <div className="space-y-1.5 mb-3">
              {(c.tasks || []).length === 0 && <p className="text-xs text-[#1e3d3a]/45">No tasks yet.</p>}
              {(c.tasks || []).slice().sort((a, b) => (a.done - b.done) || (a.due || "9999").localeCompare(b.due || "9999")).map((t) => {
                const overdue = !t.done && t.due && t.due < today();
                return (
                  <div key={t.id} data-testid={`crm-task-${t.id}`} className="flex items-center gap-2 rounded-lg border border-[#1e3d3a]/10 px-2.5 py-1.5">
                    <button data-testid={`crm-task-toggle-${t.id}`} onClick={() => toggleTask(t)} className="text-[#c9a961]">{t.done ? <CheckSquare className="h-4 w-4" /> : <Square className="h-4 w-4 text-[#1e3d3a]/35" />}</button>
                    <span className={`flex-1 text-sm ${t.done ? "line-through text-[#1e3d3a]/40" : "text-[#1e3d3a]/80"}`}>{t.title}</span>
                    {t.due && <span className={`text-[10px] ${overdue ? "text-[#b23a48] font-semibold" : "text-[#1e3d3a]/45"}`}>{t.due}</span>}
                    <button data-testid={`crm-task-del-${t.id}`} onClick={() => delTask(t.id)} className="text-[#1e3d3a]/30 hover:text-[#b23a48]"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                );
              })}
            </div>
            <div className="flex flex-col gap-1.5">
              <input data-testid="crm-task-title" value={taskTitle} onChange={(e) => setTaskTitle(e.target.value)} placeholder="New task…" className={inputCls} />
              <div className="flex gap-1.5">
                <input data-testid="crm-task-due" type="date" value={taskDue} onChange={(e) => setTaskDue(e.target.value)} className={inputCls} />
                <button data-testid="crm-task-add" disabled={busy} onClick={addTask} className="inline-flex items-center gap-1 rounded-lg bg-[#1e3d3a] px-3 text-sm text-white hover:bg-[#2a534d] disabled:opacity-50"><Plus className="h-4 w-4" /></button>
              </div>
            </div>
          </div>

          {/* Activity timeline */}
          <div data-testid="crm-activities">
            <h4 className="font-head text-base text-[#1e3d3a] mb-2 flex items-center gap-1.5"><StickyNote className="h-4 w-4 text-[#c9a961]" /> Activity</h4>
            <div className="space-y-2 mb-3 max-h-56 overflow-y-auto pr-1">
              {(c.activities || []).length === 0 && <p className="text-xs text-[#1e3d3a]/45">No activity logged yet.</p>}
              {(c.activities || []).map((a) => {
                const Icon = ACT_ICON[a.type] || StickyNote;
                return (
                  <div key={a.id} data-testid={`crm-activity-${a.id}`} className="flex gap-2 group">
                    <div className="mt-0.5 text-[#c9a961]"><Icon className="h-3.5 w-3.5" /></div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-[#1e3d3a]/80">{a.note}</div>
                      <div className="text-[10px] text-[#1e3d3a]/40 uppercase tracking-wide">{a.type} · {a.date}</div>
                    </div>
                    <button data-testid={`crm-activity-del-${a.id}`} onClick={() => delActivity(a.id)} className="opacity-0 group-hover:opacity-100 text-[#1e3d3a]/30 hover:text-[#b23a48] transition-opacity"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                );
              })}
            </div>
            <div className="flex flex-col gap-1.5">
              <div className="flex gap-1.5">
                <select data-testid="crm-activity-type" value={act.type} onChange={(e) => setAct({ ...act, type: e.target.value })} className={inputCls + " flex-1"}>
                  {["note", "call", "email", "meeting"].map((t) => <option key={t} value={t}>{t[0].toUpperCase() + t.slice(1)}</option>)}
                </select>
                <input data-testid="crm-activity-date" type="date" value={act.date} onChange={(e) => setAct({ ...act, date: e.target.value })} className={inputCls + " flex-1"} />
              </div>
              <textarea data-testid="crm-activity-note" value={act.note} onChange={(e) => setAct({ ...act, note: e.target.value })} rows={2} placeholder="Log a note, call, email or meeting…" className={inputCls} />
              <button data-testid="crm-activity-add" disabled={busy} onClick={addActivity} className="self-end inline-flex items-center gap-1 rounded-full bg-[#c9a961] px-3.5 py-1.5 text-sm font-semibold text-[#1a2e2b] hover:bg-[#d8b972] disabled:opacity-50"><Plus className="h-4 w-4" /> Log</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------- Custom pipeline stages editor ----------------
function StagesModal({ initial, onClose, onSaved }) {
  const [stages, setStages] = useState(initial.length ? initial : ["New", "Won", "Lost"]);
  const [busy, setBusy] = useState(false);
  const set = (i, v) => setStages((a) => a.map((x, j) => (j === i ? v : x)));
  const add = () => setStages((a) => [...a, ""]);
  const remove = (i) => setStages((a) => a.filter((_, j) => j !== i));

  const save = async () => {
    const clean = stages.map((s) => s.trim()).filter(Boolean);
    if (clean.length < 2) return toast.error("Add at least two stages.");
    setBusy(true);
    try { await api.put("/portal/crm/settings/stages", { stages: clean }); toast.success("Pipeline updated."); await onSaved(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save."); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="crm-stages-modal" onClick={onClose}>
      <div className="w-full max-w-sm rounded-2xl bg-white p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1"><h3 className="font-head text-xl text-[#1e3d3a]">Pipeline stages</h3><button data-testid="crm-stages-close" onClick={onClose} className="text-[#1e3d3a]/40 hover:text-[#1e3d3a]"><X className="h-5 w-5" /></button></div>
        <p className="text-xs text-[#1e3d3a]/50 mb-4">Rename or reorder how deals flow. Tip: keep a “Won” and “Lost” stage so win-rate stays accurate.</p>
        <div className="space-y-2">
          {stages.map((st, i) => (
            <div key={i} className="flex items-center gap-2">
              <GripVertical className="h-4 w-4 text-[#1e3d3a]/25" />
              <input data-testid={`crm-stage-input-${i}`} value={st} onChange={(e) => set(i, e.target.value)} className={inputCls} />
              <button data-testid={`crm-stage-remove-${i}`} onClick={() => remove(i)} disabled={stages.length <= 2} className="text-[#1e3d3a]/30 hover:text-[#b23a48] disabled:opacity-30"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
        <button data-testid="crm-stage-add" onClick={add} disabled={stages.length >= 10} className="mt-3 inline-flex items-center gap-1 text-sm text-[#1e3d3a]/60 hover:text-[#c9a961] disabled:opacity-40"><Plus className="h-4 w-4" /> Add stage</button>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-full border border-[#1e3d3a]/20 px-4 py-2 text-sm text-[#1e3d3a]/70">Cancel</button>
          <button data-testid="crm-stages-save" disabled={busy} onClick={save} className="inline-flex items-center gap-1.5 rounded-full bg-[#c9a961] px-4 py-2 text-sm font-semibold text-[#1a2e2b] hover:bg-[#d8b972] disabled:opacity-50">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save</button>
        </div>
      </div>
    </div>
  );
}
